import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../../components/DatePicker';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import { useKasa } from '../../context/KasaContext';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import OnayPopup from '../../components/OnayPopup';

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const YIL_LISTESI = Array.from({ length: 16 }, (_, i) => 2015 + i); // 2015 - 2030

function bugunISO() {
  var d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
}
function ddmmyyyy(iso) { var p = iso.split('-'); return p[2]+'.'+p[1]+'.'+p[0]; }
function isoToDate(iso) { var p = iso.split('-').map(Number); return new Date(p[0],p[1]-1,p[2]); }
function dateToISO(d) { return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); }
function ddmmyyyyToISO(s) { var p = s.split('.'); return p[2]+'-'+p[1]+'-'+p[0]; }
function isoToDDMMYYYY(iso) { var p = iso.split('-'); return p[2]+'.'+p[1]+'.'+p[0]; }

// ── Helper UI Components ──────────────────────────────────────────────────
function AyDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  function handleOpen() {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(p => !p);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="h-12 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-100 rounded-2xl px-6 text-sm font-black flex items-center gap-3 transition-all active:scale-95 shadow-sm min-w-[160px] justify-between"
      >
        <span className="uppercase tracking-tighter">{AYLAR[value]}</span>
        <span className={`text-[10px] transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && createPortal(
        <div
          style={{ position: 'absolute', top: pos.top + 8, left: pos.left, width: Math.max(pos.width, 160), zIndex: 9999 }}
          className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-[2rem] shadow-2xl overflow-hidden animate-fadeIn"
          onMouseDown={e => e.stopPropagation()}
        >
          <div className="max-h-64 overflow-y-auto py-3 custom-scrollbar">
            {AYLAR.map((ay, i) => (
              <button
                key={i}
                onClick={() => { onChange(i); setOpen(false); }}
                className={`w-full text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${
                  i === value ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
                }`}
              >
                {ay}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function YilDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  function handleOpen() {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(p => !p);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="h-12 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-100 rounded-2xl px-6 text-sm font-black flex items-center gap-3 transition-all active:scale-95 shadow-sm min-w-[110px] justify-between"
      >
        <span className="tracking-tighter">{value}</span>
        <span className={`text-[10px] transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && createPortal(
        <div
          style={{ position: 'absolute', top: pos.top + 8, left: pos.left, width: Math.max(pos.width, 120), zIndex: 9999 }}
          className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-[2rem] shadow-2xl overflow-hidden animate-fadeIn"
          onMouseDown={e => e.stopPropagation()}
        >
          <div className="max-h-64 overflow-y-auto py-3 custom-scrollbar">
            {YIL_LISTESI.map((yil) => (
              <button
                key={yil}
                onClick={() => { onChange(yil); setOpen(false); }}
                className={`w-full text-left px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${
                  yil === value ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
                }`}
              >
                {yil}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function DateField({ value, onChange, minDate }) {
  function shift(delta) {
    const d = isoToDate(ddmmyyyyToISO(value));
    d.setDate(d.getDate() + delta);
    onChange(isoToDDMMYYYY(dateToISO(d)));
  }
  return (
    <div className="flex items-center group">
      <button onClick={() => shift(-1)} className="w-10 h-12 bg-white hover:bg-gray-50 border-2 border-r-0 border-gray-100 rounded-l-2xl flex items-center justify-center text-gray-400 transition-all active:scale-95 shadow-sm group-hover:border-indigo-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
      </button>
      <div className="border-2 border-gray-100 bg-white h-12 flex items-center shadow-sm group-hover:border-indigo-100 transition-colors">
        <DatePicker value={value} onChange={onChange} minDate={minDate} className="rounded-none border-0 px-4 text-xs font-black text-gray-700 min-w-[130px]" />
      </div>
      <button onClick={() => shift(1)} className="w-10 h-12 bg-white hover:bg-gray-50 border-2 border-l-0 border-gray-100 rounded-r-2xl flex items-center justify-center text-gray-400 transition-all active:scale-95 shadow-sm group-hover:border-indigo-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
      </button>
    </div>
  );
}

function EyeIcon({ color }) {
  return (
    <div className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:bg-gray-100 active:scale-90" style={{ color: color || '#d1d5db' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

// ── Adisyon Popup ──────────────────────────────────────────────────────────
function AdisyonPopup({ iso, onClose, adisyonlar, dovizCevir, paraBirimi }) {
  const kayitlar = adisyonlar.filter(a => a.tarih === ddmmyyyy(iso));
  
  // Adisyonlar genellikle TL bazlıdır; ancak ödeme tiplerine göre kırılım yapabiliriz.
  // Burada kullanıcı tüm modallarda "Döviz Kırılımı" istediği için TL bazlı olsa da bu yapıyı koruyoruz.
  const dovizOzet = {};
  kayitlar.filter(a => a.odemeyapildiMi).forEach(a => {
    const pb = 'TL';
    if (!dovizOzet[pb]) dovizOzet[pb] = { original: 0, tl: 0 };
    dovizOzet[pb].original += (a.toplamTutar || 0);
    dovizOzet[pb].tl += (a.toplamTutar || 0); // Adisyon TL'dir
  });

  const toplamOdendi   = Object.values(dovizOzet).reduce((s, v) => s + v.tl, 0);
  const toplamBekliyor = kayitlar.filter(a => !a.odemeyapildiMi).reduce((s, a) => s + (a.toplamTutar || 0), 0);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9000] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-[#f8fafc] w-full max-w-5xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        <div className="bg-gradient-to-br from-orange-500 to-amber-700 px-4 sm:px-8 py-4 sm:py-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl shadow-xl">🍽️</div>
            <div>
                <h2 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Günlük Adisyonlar</h2>
                <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">{ddmmyyyy(iso)} Tarihli Kayıtlar</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
          </div>
        </div>

        <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 flex-1 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              {kayitlar.length > 0 ? `Toplam ${kayitlar.length} Kayıt Bulundu` : 'Kayıt Bulunamadı'}
            </p>
            <div className="flex items-center gap-2">
               <button onClick={() => downloadExcel('adisyonlar-' + ddmmyyyy(iso), 'Adisyonlar', ['Tarih','Rez. No','Ürün/Hizmet','Miktar','Birim Fiyat (TL)','Toplam (TL)','Durum'], kayitlar.map(a => [a.tarih, '#'+a.rezervasyonId, a.stokAdi, a.miktar, (a.birimFiyat||0).toFixed(2), (a.toplamTutar||0).toFixed(2), a.odemeyapildiMi ? 'Ödendi' : 'Bekliyor']))} className="h-10 px-6 bg-white border-2 border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-500 hover:border-orange-200 hover:text-orange-600 transition-all shadow-sm">Excel</button>
               <button onClick={() => printTable('Adisyon Hareketleri', ['Tarih','Rez. No','Ürün/Hizmet','Miktar','Birim Fiyat','Toplam','Durum'], kayitlar.map(a => [a.tarih, '#'+a.rezervasyonId, a.stokAdi, a.miktar, (a.birimFiyat||0).toFixed(2)+' TL', (a.toplamTutar||0).toFixed(2)+' TL', a.odemeyapildiMi ? 'Ödendi' : 'Bekliyor']), ddmmyyyy(iso) + ' · ' + kayitlar.length + ' kayıt')} className="h-10 px-6 bg-white border-2 border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-500 hover:border-orange-200 hover:text-orange-600 transition-all shadow-sm">Yazdır</button>
            </div>
          </div>

          <div className="flex-1 overflow-auto custom-scrollbar border-2 border-gray-50 rounded-[2rem] bg-white">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white/80 backdrop-blur-md z-10">
                <tr className="border-b border-gray-100">
                  {['Tarih','Rez. No','Ürün/Hizmet','Miktar','Birim Fiyat','Toplam','Durum'].map(h => (
                    <th key={h} className="text-left py-5 px-6 font-black text-gray-400 text-[10px] uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {kayitlar.length === 0 ? (
                  <EmptyState as="td" colSpan={7} text="Kayıt Bulunamadı" />
                ) : kayitlar.map(a => (
                  <tr key={a.adisyonId} className="border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                    <td className="py-4 px-6 text-[11px] font-bold text-gray-500">{a.tarih}</td>
                    <td className="py-4 px-6">
                      <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[10px] font-black">#{a.rezervasyonId}</span>
                    </td>
                    <td className="py-4 px-6 text-xs font-black text-gray-900 tracking-tighter">{a.stokAdi}</td>
                    <td className="py-4 px-6 text-[11px] font-bold text-gray-500">{a.miktar}</td>
                    <td className="py-4 px-6 text-[11px] font-bold text-gray-500">{(a.birimFiyat||0).toLocaleString('tr-TR',{minimumFractionDigits:2})} TL</td>
                    <td className="py-4 px-6 font-black text-orange-600 text-sm">{(a.toplamTutar||0).toLocaleString('tr-TR',{minimumFractionDigits:2})} TL</td>
                    <td className="py-4 px-6">
                      {a.odemeyapildiMi
                        ? <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-[10px] font-black uppercase">Ödendi</span>
                        : <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">Bekliyor</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {kayitlar.length > 0 && (
            <div className="bg-gray-50 rounded-[2rem] p-3 sm:p-6 flex-shrink-0 flex flex-col md:flex-row justify-between items-end md:items-center gap-4 sm:gap-8 border border-gray-100 shadow-inner">
              <div className="flex flex-wrap gap-6">
                {Object.entries(dovizOzet).map(([pb, val]) => (
                  <div key={pb} className="bg-white px-4 py-2 rounded-2xl border border-gray-100 shadow-sm">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{pb} Toplam</p>
                    <p className="text-sm font-black text-gray-800 tracking-tighter">
                      {val.original.toLocaleString('tr-TR', {minimumFractionDigits:2})} 
                      <span className="text-[10px] ml-1 opacity-50">{pb}</span>
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 sm:gap-12">
                <div className="flex flex-col items-end">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">✅ Tahsil Edilen (TL)</p>
                  <p className="text-lg sm:text-xl font-black text-emerald-600 tracking-tighter">{toplamOdendi.toLocaleString('tr-TR',{minimumFractionDigits:2})} <span className="text-xs opacity-60">TL</span></p>
                </div>
                {toplamBekliyor > 0 && (
                  <div className="flex flex-col items-end">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">⏳ Bekleyen (TL)</p>
                    <p className="text-lg sm:text-xl font-black text-amber-600 tracking-tighter">{toplamBekliyor.toLocaleString('tr-TR',{minimumFractionDigits:2})} <span className="text-xs opacity-60">TL</span></p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Gelir Popup ────────────────────────────────────────────────────────────
function GelirPopup({ iso, gelirTipFiltre, kasaGelirler, odemeler, onClose, dovizCevir, paraBirimi, gelirSil, odemeSil }) {
  const baslik = gelirTipFiltre === 'rezervasyon' ? 'Günlük Ödemeler'
             : gelirTipFiltre === 'gelir'        ? 'Günlük Kasa Gelirleri'
             : 'Günlük Gelirler';

  const tumKayitlar = [];
  kasaGelirler.filter(g => g.tarih === ddmmyyyy(iso)).forEach(g => {
    tumKayitlar.push({ id: 'gelir-'+g.id, tip: 'gelir', tarih: g.tarih, saat: g.saat, misafir: g.personel||'', tutar: g.tutar, paraBirimi: g.paraBirimi||'TL', odemeTip: g.odemeTip, aciklama: g.ad, rezNo: null, kurDegeri: g.kurDegeri });
  });
  odemeler.filter(o => o.tarih === ddmmyyyy(iso)).forEach(o => {
    tumKayitlar.push({ id: 'odeme-'+o.odemeId, tip: 'rezervasyon', tarih: o.tarih, saat: o.saat, misafir: o.personel||'', tutar: o.tutar, paraBirimi: o.paraBirimi||'TL', odemeTip: o.odemeTip, aciklama: o.ad, rezNo: o.rezervasyonId, kurDegeri: o.kurDegeri });
  });

  const kayitlar = gelirTipFiltre ? tumKayitlar.filter(k => k.tip === gelirTipFiltre) : tumKayitlar;
  const [detayRezId, setDetayRezId] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const dovizOzet = {};

  kayitlar.forEach(k => { 
    const pBirim = k.paraBirimi || 'TL';
    const kur = k.kurDegeri || 1;
    const tlKarsiligi = k.tutar * kur;

    // Döviz Kırılımı bazlı (Orijinal + Tarihi TL Karşılığı)
    if (!dovizOzet[pBirim]) dovizOzet[pBirim] = { original: 0, tl: 0 };
    dovizOzet[pBirim].original += k.tutar;
    dovizOzet[pBirim].tl += tlKarsiligi;
  });

  const genelToplamTL = Object.values(dovizOzet).reduce((s, v) => s + v.tl, 0);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9000] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
        <div className="bg-[#f8fafc] w-full max-w-6xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
          
          <div className="bg-gradient-to-br from-emerald-500 to-teal-700 px-4 sm:px-8 py-4 sm:py-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl shadow-xl">💰</div>
              <div>
                  <h2 className="text-xl font-black text-white tracking-tighter uppercase leading-none">{baslik}</h2>
                  <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">{ddmmyyyy(iso)} Tahsilat Akışı</p>
              </div>
            </div>
            <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
          </div>

          <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {kayitlar.length > 0 ? `${kayitlar.length} İşlem Listeleniyor` : 'İşlem Bulunmamaktadır'}
              </p>
              <div className="flex items-center gap-2">
                 <button onClick={() => downloadExcel('gelir-raporu-' + ddmmyyyy(iso), 'Gelirler', ['Tarih','Saat','Misafir/Personel','Tutar','Para Birimi','Ödeme Tipi','Açıklama','Rez. No'], kayitlar.map(k => [k.tarih, k.saat, k.misafir||'—', k.tutar.toLocaleString('tr-TR',{minimumFractionDigits:2}), k.paraBirimi, k.odemeTip, k.aciklama||'', k.rezNo ? '#'+k.rezNo : '—']))} className="h-10 px-6 bg-white border-2 border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-500 hover:border-emerald-200 hover:text-emerald-600 transition-all shadow-sm">📊 Rapor Al</button>
                 <button onClick={() => printTable(baslik, ['Tarih','Saat','Misafir','Tutar','PB','Ödeme','Açıklama','Rez. No'], kayitlar.map(k => [k.tarih, k.saat, k.misafir||'—', k.tutar.toLocaleString('tr-TR',{minimumFractionDigits:2})+' '+k.paraBirimi, k.paraBirimi, k.odemeTip, k.aciklama||'', k.rezNo ? '#'+k.rezNo : '—']), ddmmyyyy(iso) + ' · ' + kayitlar.length + ' kayıt')} className="h-10 px-6 bg-white border-2 border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-500 hover:border-emerald-200 hover:text-emerald-600 transition-all shadow-sm">🖨️ Yazdır</button>
              </div>
            </div>

            <div className="flex-1 overflow-auto custom-scrollbar border-2 border-gray-50 rounded-[2rem] bg-white">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white/80 backdrop-blur-md z-10">
                  <tr className="border-b border-gray-100">
                    {['Tarih','Saat','Misafir/Personel','Tutar','Birim','Tip','Açıklama','Rez. No','Aksiyon'].map(h => (
                      <th key={h} className="text-left py-5 px-6 font-black text-gray-400 text-[10px] uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kayitlar.length === 0 ? (
                    <EmptyState as="td" colSpan={9} text="Kayıt Bulunamadı" />
                  ) : kayitlar.map(k => (
                    <tr key={k.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                      <td className="py-4 px-6 text-[10px] font-bold text-gray-400">{k.tarih}</td>
                      <td className="py-4 px-6 text-[10px] font-bold text-gray-400">{k.saat}</td>
                      <td className="py-4 px-6 text-[11px] font-black text-indigo-600 uppercase tracking-tighter">{k.misafir||'—'}</td>
                      <td className="py-4 px-6 text-sm font-black text-gray-900 tracking-tighter">{k.tutar.toLocaleString('tr-TR',{minimumFractionDigits:2})}</td>
                      <td className="py-4 px-6 text-[10px] font-black text-gray-400">{k.paraBirimi}</td>
                      <td className="py-4 px-6">
                        <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">{k.odemeTip}</span>
                      </td>
                      <td className="py-4 px-6 text-[11px] font-bold text-gray-500 max-w-[150px] truncate">{k.aciklama}</td>
                      <td className="py-4 px-6">
                        {k.rezNo ? <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-lg text-[10px] font-black">#{k.rezNo}</span> : '—'}
                      </td>
                      <td className="py-4 px-6 flex items-center gap-2">
                        {k.rezNo && (
                          <button onClick={() => setDetayRezId(k.rezNo)} className="bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl transition-all shadow-lg shadow-indigo-100 active:scale-95 leading-none">Detay</button>
                        )}
                        <button onClick={() => setSilOnay(k)} className="bg-rose-50 hover:bg-rose-500 text-rose-500 hover:text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl transition-all shadow-sm active:scale-95 leading-none">Sil</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {kayitlar.length > 0 && (
              <div className="bg-gray-50 rounded-[2rem] p-3 sm:p-6 flex-shrink-0 border border-gray-100 shadow-inner">
                <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4 sm:gap-6">
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(dovizOzet).filter(([_, v]) => v.original > 0).map(([pb, val]) => (
                      <div key={pb} className="bg-white px-5 py-3 rounded-[1.5rem] border border-gray-200 shadow-sm flex items-center gap-4 transition-all hover:border-emerald-200 hover:shadow-md">
                         <div className="flex flex-col">
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest leading-tight">{pb} TOPLAM</p>
                            <p className="text-base font-black text-gray-800 tracking-tighter">{val.original.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-[10px] opacity-50">{pb}</span></p>
                         </div>
                         {pb !== 'TL' && (
                           <div className="pl-4 border-l border-gray-100 flex flex-col">
                              <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest leading-tight">TL KARŞILIĞI</p>
                              <p className="text-sm font-black text-emerald-600 tracking-tighter">≈ {val.tl.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</p>
                           </div>
                         )}
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col items-end bg-emerald-50/50 px-4 sm:px-8 py-2 sm:py-4 rounded-[2rem] border border-emerald-100/50">
                    <p className="text-[10px] font-black text-emerald-700/50 uppercase tracking-[0.2em] mb-1">📈 GÜNLÜK TOPLAM (TARİHİ TL)</p>
                    <p className="text-2xl sm:text-4xl font-black text-emerald-600 tracking-tighter leading-none">
                      {genelToplamTL.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-sm opacity-60">TL</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {detayRezId && <RezervasyonDetayModal rezervasyonId={detayRezId} onClose={() => setDetayRezId(null)} />}
      <OnayPopup
        acik={!!silOnay}
        baslik="Gelir Kaydını Sil"
        mesaj={silOnay ? `"${silOnay.aciklama || 'Bu kayıt'}" silinecek. Devam etmek istiyor musunuz?` : ''}
        onEvet={async () => {
          if (silOnay) {
            const rawId = String(silOnay.id);
            if (rawId.startsWith('odeme-')) await odemeSil(rawId.replace('odeme-', ''));
            else await gelirSil(rawId.replace('gelir-', ''));
          }
          setSilOnay(null);
        }}
        onHayir={() => setSilOnay(null)}
      />
    </>,
    document.body
  );
}

// ── Gider Popup ────────────────────────────────────────────────────────────
function GiderPopup({ iso, onClose, giderler, dovizCevir, paraBirimi, giderSil }) {
  const tumKayitlar = giderler.filter(g => g.tarih === ddmmyyyy(iso));
  const [filtreler, setFiltreler] = useState({ tarihSaat:'', ad:'', tutar:'', toplam:'', personel:'' });
  const [silOnayG, setSilOnayG] = useState(null);

  const kayitlar = tumKayitlar.filter(k => (
    (!filtreler.tarihSaat || (k.tarihSaat||'').toLocaleLowerCase('tr-TR').includes(filtreler.tarihSaat.toLocaleLowerCase('tr-TR'))) &&
    (!filtreler.ad        || (k.ad||'').toLocaleLowerCase('tr-TR').includes(filtreler.ad.toLocaleLowerCase('tr-TR'))) &&
    (!filtreler.tutar     || String(k.tutar).includes(filtreler.tutar)) &&
    (!filtreler.toplam    || String(k.toplam).includes(filtreler.toplam)) &&
    (!filtreler.personel  || (k.personel||'').toLocaleLowerCase('tr-TR').includes(filtreler.personel.toLocaleLowerCase('tr-TR')))
  ));
  const dovizOzet = {};
  kayitlar.forEach(k => { 
    const pBirim = k.paraBirimi || 'TL';
    const kur = k.kurDegeri || 1;
    const tutar = k.toplam || k.tutar || 0;
    const tlKarsiligi = tutar * kur;

    // Döviz Kırılımı bazlı
    if (!dovizOzet[pBirim]) dovizOzet[pBirim] = { original: 0, tl: 0 };
    dovizOzet[pBirim].original += tutar;
    dovizOzet[pBirim].tl += tlKarsiligi;
  });

  const genelToplamTL = Object.values(dovizOzet).reduce((s, v) => s + v.tl, 0);
  const setF = (key, val) => setFiltreler(p => ({ ...p, [key]: val }));

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return createPortal(
    <>
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9000] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-[#f8fafc] w-full max-w-6xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        <div className="bg-gradient-to-br from-rose-500 to-rose-700 px-4 sm:px-8 py-4 sm:py-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl shadow-xl">📉</div>
            <div>
                <h2 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Günlük Giderler</h2>
                <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">{ddmmyyyy(iso)} Sarfiyat Listesi</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
        </div>

        <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 flex-1 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between">
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {kayitlar.length > 0 ? `${kayitlar.length} Gider Kaydı Bulundu` : 'Gider Kaydı Bulunamadı'}
              </p>
          </div>

          <div className="flex-1 overflow-auto custom-scrollbar border-2 border-gray-50 rounded-[2rem] bg-white">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white shadow-sm z-10">
                <tr className="border-b border-gray-100">
                  {['Tarih/Saat','Açıklama','Ödeme Tipi','Birim','Fiyat','Toplam','Para','Personel','Aksiyon'].map(h => (
                    <th key={h} className="text-left py-5 px-6 font-black text-gray-400 text-[10px] uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
                <tr className="bg-gray-50/50">
                  {[
                    { key:'tarihSaat', ph:'Filtre' }, { key:'ad', ph:'Filtre' }, { key:null }, { key:null },
                    { key:'tutar', ph:'Filtre' }, { key:'toplam', ph:'Filtre' }, { key:null }, { key:'personel', ph:'Filtre' }, { key:null }
                  ].map((col, i) => (
                    <td key={i} className="py-2 px-6">
                      {col.key && (
                        <input value={filtreler[col.key]} onChange={e => setF(col.key, e.target.value)} 
                          placeholder={col.ph} className="w-full bg-white border-2 border-gray-100 rounded-lg px-2 py-1 text-[10px] font-bold focus:outline-none focus:border-rose-300 transition-colors" />
                      )}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {kayitlar.length === 0 ? (
                  <EmptyState as="td" colSpan={8} text="Gider Bulunamadı" />
                ) : kayitlar.map(k => (
                  <tr key={k.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                    <td className="py-4 px-6 text-[10px] font-black text-gray-400 whitespace-nowrap uppercase tracking-tighter">{k.tarih} {k.saat}</td>
                    <td className="py-4 px-6 text-[11px] font-black text-gray-800 tracking-tighter">{k.ad}</td>
                    <td className="py-4 px-6"><span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">{k.odemeTip}</span></td>
                    <td className="py-4 px-6 text-[11px] font-bold text-gray-500">{k.miktar}</td>
                    <td className="py-4 px-6 text-[11px] font-bold text-gray-500">{(k.tutar||0).toLocaleString('tr-TR',{minimumFractionDigits:2})}</td>
                    <td className="py-4 px-6 text-sm font-black text-rose-600 tracking-tighter">{(k.toplam||0).toLocaleString('tr-TR',{minimumFractionDigits:2})}</td>
                    <td className="py-4 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">{k.paraBirimi}</td>
                    <td className="py-4 px-6 text-[11px] font-black text-indigo-600 uppercase tracking-tighter">{k.personel}</td>
                    <td className="py-4 px-6">
                      <button onClick={() => setSilOnayG(k)} className="bg-rose-50 hover:bg-rose-500 text-rose-500 hover:text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl transition-all shadow-sm active:scale-95 leading-none">Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {kayitlar.length > 0 && (
            <div className="bg-gray-50 rounded-[2rem] p-3 sm:p-6 flex-shrink-0 border border-gray-100 shadow-inner">
              <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4 sm:gap-6">
                <div className="flex flex-wrap gap-4">
                  {Object.entries(dovizOzet).filter(([_, v]) => v.original > 0).map(([pb, val]) => (
                    <div key={pb} className="bg-white px-5 py-3 rounded-[1.5rem] border border-gray-200 shadow-sm flex items-center gap-4 transition-all hover:border-rose-200 hover:shadow-md">
                       <div className="flex flex-col">
                          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest leading-tight">{pb} TOPLAM</p>
                          <p className="text-base font-black text-gray-800 tracking-tighter">{val.original.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-[10px] opacity-50">{pb}</span></p>
                       </div>
                       {pb !== 'TL' && (
                         <div className="pl-4 border-l border-gray-100 flex flex-col">
                            <p className="text-[8px] font-black text-rose-500 uppercase tracking-widest leading-tight">TL KARŞILIĞI</p>
                            <p className="text-sm font-black text-rose-600 tracking-tighter">≈ {val.tl.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</p>
                         </div>
                       )}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-end bg-rose-50/50 px-4 sm:px-8 py-2 sm:py-4 rounded-[2rem] border border-rose-100/50">
                  <p className="text-[10px] font-black text-rose-700/50 uppercase tracking-[0.2em] mb-1">📉 GÜNLÜK TOPLAM (TARİHİ TL)</p>
                  <p className="text-2xl sm:text-4xl font-black text-rose-600 tracking-tighter leading-none">
                    {genelToplamTL.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-sm opacity-60">TL</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    <OnayPopup
      acik={!!silOnayG}
      baslik="Gider Kaydını Sil"
      mesaj={silOnayG ? `"${silOnayG.ad || 'Bu kayıt'}" silinecek. Devam etmek istiyor musunuz?` : ''}
      onEvet={async () => {
        if (silOnayG) await giderSil(silOnayG.id);
        setSilOnayG(null);
      }}
      onHayir={() => setSilOnayG(null)}
    />
    </>,
    document.body
  );
}

// ── Ana Bileşen ──
export default function AylikKasa() {
  const bugunDate = new Date();
  const [secilenYil, setSecilenYil] = useState(bugunDate.getFullYear());
  const [secilenAy,  setSecilenAy]  = useState(bugunDate.getMonth());

  const [basTarih, setBasTarih] = useState(() => isoToDDMMYYYY(bugunDate.getFullYear()+'-'+String(bugunDate.getMonth()+1).padStart(2,'0')+'-01'));
  const [bitTarih, setBitTarih] = useState(() => isoToDDMMYYYY(dateToISO(new Date(bugunDate.getFullYear(), bugunDate.getMonth()+1, 0))));
  
  const [personelFiltre, setPersonelFiltre] = useState('Hepsi');
  const [detayFiltre,    setDetayFiltre]    = useState('Göster');
  const [sifirGizle,     setSifirGizle]     = useState(false);
  const [paraBirimi,     setParaBirimi]     = useState('TL');
  const [aktifBas,       setAktifBas]       = useState(basTarih);
  const [aktifBit,       setAktifBit]       = useState(bitTarih);
  const [secilenGun,     setSecilenGun]     = useState(null);

  const {
    gelirler, giderler, adisyonlar, personeller,
    paraBirimleri: pbListesi, dovizKurlari, yukleniyor, fetchVeriler,
    gelirSil, odemeSil, giderSil
  } = useKasa();

  useEffect(() => { fetchVeriler(); }, []);

  const paraBirimiListesi = pbListesi.length > 0 ? pbListesi.map(p => p.tcmbKod || p.ad) : ['TL', 'EURO', 'USD'];
  const personelListesi = personeller.map(p => p.adSoyad);

  useEffect(() => {
    if (paraBirimiListesi.length > 0 && !paraBirimiListesi.includes(paraBirimi)) {
      setParaBirimi(paraBirimiListesi.find(p => p === 'TRY' || p === 'TL') || paraBirimiListesi[0]);
    }
  }, [pbListesi]);

  function dovizCevir(tutar, kaynakKod, kayitKurDegeri) {
    if (!tutar || tutar === 0) return 0;
    const kaynak = kaynakKod || 'TL';
    const hedef  = paraBirimi;
    const kaynakTlOrani = (kayitKurDegeri && kayitKurDegeri > 0 && kaynak !== 'TL' && kaynak !== 'TRY')
      ? kayitKurDegeri
      : (dovizKurlari[kaynak] || 1);
    return (tutar * kaynakTlOrani) / (dovizKurlari[hedef] || 1);
  }

  const odemeler     = gelirler.filter(g => g.kaynak === 'rezervasyon' || g.kaynak === 'walk-in' || g.kaynak === 'havuz');
  const kasaGelirler = gelirler.filter(g => g.kaynak === 'kasa');

  function handleAySecim(ayIdx) {
    setSecilenAy(ayIdx);
    const bas = isoToDDMMYYYY(secilenYil+'-'+String(ayIdx+1).padStart(2,'0')+'-01');
    const bit = isoToDDMMYYYY(dateToISO(new Date(secilenYil, ayIdx+1, 0)));
    setBasTarih(bas); setBitTarih(bit); setAktifBas(bas); setAktifBit(bit);
  }
  function handleYilSecim(yil) {
    setSecilenYil(yil);
    const bas = isoToDDMMYYYY(yil+'-'+String(secilenAy+1).padStart(2,'0')+'-01');
    const bit = isoToDDMMYYYY(dateToISO(new Date(yil, secilenAy+1, 0)));
    setBasTarih(bas); setBitTarih(bit); setAktifBas(bas); setAktifBit(bit);
  }
  function filtrele() { setAktifBas(basTarih); setAktifBit(bitTarih); }
  function temizle() {
    const bas = isoToDDMMYYYY(secilenYil+'-'+String(secilenAy+1).padStart(2,'0')+'-01');
    const bit = isoToDDMMYYYY(dateToISO(new Date(secilenYil, secilenAy+1, 0)));
    setBasTarih(bas); setBitTarih(bit);
    setPersonelFiltre('Hepsi'); setDetayFiltre('Göster'); setSifirGizle(false);
    setAktifBas(bas); setAktifBit(bit);
  }

  const gunler = [];
  const cur = new Date(isoToDate(ddmmyyyyToISO(aktifBas)));
  const bitD = isoToDate(ddmmyyyyToISO(aktifBit));
  while (cur <= bitD) { gunler.push(dateToISO(cur)); cur.setDate(cur.getDate()+1); }

  function gunOzet(iso) {
    const gunStr = ddmmyyyy(iso);
    const gunKasaGelirler = kasaGelirler.filter(g => g.tarih === gunStr);
    const gunOdemeler     = odemeler.filter(o => o.tarih === gunStr);
    const gunGiderler     = giderler.filter(g => g.tarih === gunStr);
    const gunAdisyonlar   = adisyonlar.filter(a => a.tarih === gunStr);

    const filtKasaGelirler = personelFiltre === 'Hepsi' ? gunKasaGelirler : gunKasaGelirler.filter(g => g.personel === personelFiltre);
    const filtOdemeler     = personelFiltre === 'Hepsi' ? gunOdemeler     : gunOdemeler.filter(o => o.personel === personelFiltre);
    const filtGiderler     = personelFiltre === 'Hepsi' ? gunGiderler     : gunGiderler.filter(g => g.personel === personelFiltre);
    const filtAdisyonlar   = personelFiltre === 'Hepsi' ? gunAdisyonlar   : gunAdisyonlar.filter(a => a.personelAd === personelFiltre);

    const adisyonOdendi   = filtAdisyonlar.filter(a => a.odemeyapildiMi).reduce((s, a) => s + dovizCevir(a.toplamTutar || 0, 'TL'), 0);
    const adisyonBekliyor = filtAdisyonlar.filter(a => !a.odemeyapildiMi).reduce((s, a) => s + dovizCevir(a.toplamTutar || 0, 'TL'), 0);
    const adisyonToplam = adisyonOdendi;
    const rezToplam     = filtOdemeler.filter(o => !o.isAdisyonOdemesi).reduce((s, o) => s + dovizCevir(o.tutar || 0, o.paraBirimi, o.kurDegeri), 0);
    const gelirToplam   = filtKasaGelirler.reduce((s, g) => s + dovizCevir(g.tutar || 0, g.paraBirimi, g.kurDegeri), 0);
    const giderToplam   = filtGiderler.reduce((s, g) => s + dovizCevir(g.toplam || g.tutar || 0, g.paraBirimi, g.kurDegeri), 0);
    const toplamGelir   = adisyonToplam + rezToplam + gelirToplam;
    
    return {
      adisyon: adisyonToplam, adisyonBekliyor, gelir: gelirToplam, rezervasyon: rezToplam,
      toplam: toplamGelir, gider: giderToplam, kasa: toplamGelir - giderToplam,
      hamKasaGelirler: filtKasaGelirler, hamOdemeler: filtOdemeler, hamGiderler: filtGiderler, hamAdisyonlar: filtAdisyonlar
    };
  }

  let toplamGelir=0, toplamGider=0, toplamKasa=0, toplamAdisyon=0, toplamAdisyonBekliyor=0, toplamRez=0, toplamEk=0;
  const gelirTipMap = {};
  const giderTipMap = {};

  gunler.forEach(iso => {
    const oz = gunOzet(iso);
    toplamAdisyon += oz.adisyon; toplamAdisyonBekliyor += oz.adisyonBekliyor;
    toplamRez += oz.rezervasyon; toplamEk += oz.gelir; toplamGelir += oz.toplam;
    toplamGider += oz.gider; toplamKasa += oz.kasa;
    oz.hamOdemeler.forEach(o => { const tip = o.odemeTip || 'DİĞER'; gelirTipMap[tip] = (gelirTipMap[tip]||0) + dovizCevir(o.tutar||0, o.paraBirimi, o.kurDegeri); });
    oz.hamKasaGelirler.forEach(g => { const tip = g.odemeTip || 'DİĞER'; gelirTipMap[tip] = (gelirTipMap[tip]||0) + dovizCevir(g.tutar||0, g.paraBirimi, g.kurDegeri); });
    if (oz.adisyon > 0) gelirTipMap['ADİSYON'] = (gelirTipMap['ADİSYON']||0) + oz.adisyon;
    oz.hamGiderler.forEach(g => { const tip = g.odemeTip || 'DİĞER'; giderTipMap[tip] = (giderTipMap[tip]||0) + dovizCevir((g.toplam||g.tutar||0), g.paraBirimi, g.kurDegeri); });
  });

  const toplamKalan = toplamGelir - toplamGider;
  const detayGoster = detayFiltre === 'Göster';

  return (
    <div className="p-8 space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
      
      {/* ── Metric Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/40 backdrop-blur-md rounded-[3rem] p-5 sm:p-8 border border-white/20 shadow-xl">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-[2rem] flex items-center justify-center text-3xl shadow-2xl shadow-indigo-100 rotate-3">📊</div>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase leading-none">Aylık Kasa</h1>
            <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mt-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> Finansal Performans Özeti
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <YilDropdown value={secilenYil} onChange={handleYilSecim} />
          <AyDropdown value={secilenAy} onChange={handleAySecim} />
        </div>
      </div>

      {/* ── Metric Cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
        {[
          { label:'Gelir', val: toplamRez, color:'blue', icon:'🏨' },
          { label:'Adisyon', val: toplamAdisyon, color:'orange', icon:'🍽️', sub: toplamAdisyonBekliyor > 0 ? `⌛ ${toplamAdisyonBekliyor.toFixed(0)} Bekliyor` : null },
          { label:'Ek Gelir', val: toplamEk, color:'indigo', icon:'➕' },
          { label:'Toplam Gelir', val: toplamGelir, color:'emerald', icon:'📈', gradient: 'from-emerald-500 to-teal-600' },
          { label:'Toplam Gider', val: toplamGider, color:'rose', icon:'📉', gradient: 'from-rose-500 to-pink-600' },
          { label:'Net Kalan', val: toplamKalan, color: toplamKalan < 0 ? 'rose' : 'indigo', icon:'💰', gradient: toplamKalan < 0 ? 'from-rose-600 to-pink-700' : 'from-indigo-600 to-blue-700' },
        ].map((k, idx) => (
          <div key={idx} className={`relative overflow-hidden group rounded-[2.5rem] p-6 shadow-xl transition-all hover:-translate-y-1 ${k.gradient ? 'text-white' : 'bg-white border-2 border-gray-50'}`}>
            {k.gradient && <div className={`absolute inset-0 bg-gradient-to-br ${k.gradient} opacity-90 group-hover:opacity-100 transition-opacity`} />}
            <div className="relative z-10">
               <div className="flex items-center justify-between mb-4">
                 <span className="text-2xl">{k.icon}</span>
                 <p className={`text-[10px] font-black uppercase tracking-widest ${k.gradient ? 'opacity-70' : 'text-gray-400'}`}>{k.label}</p>
               </div>
               <p className="text-xl font-black tracking-tighter whitespace-nowrap">
                 {k.val.toLocaleString('tr-TR',{minimumFractionDigits:2})} <span className="text-[10px] uppercase opacity-60 ml-1">{paraBirimi}</span>
               </p>
               {k.sub && <p className="text-[9px] font-black mt-2 text-amber-500 uppercase tracking-widest whitespace-nowrap">{k.sub}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* ── Filter Toolbar ────────────────────────────────────────────────── */}
      <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] p-6 border border-white/20 shadow-lg flex flex-wrap items-start gap-6">
        <div className="space-y-2 min-w-0">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Tarih Aralığı</p>
          <div className="flex flex-wrap items-center gap-3">
            <DateField value={basTarih} onChange={val => { setBasTarih(val); if (ddmmyyyyToISO(val) > ddmmyyyyToISO(bitTarih)) setBitTarih(val); }} />
            <DateField value={bitTarih} onChange={setBitTarih} minDate={basTarih} />
          </div>
        </div>
        <div className="space-y-2 min-w-0">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Personel & Görünüm</p>
          <div className="flex flex-wrap items-center gap-3">
             <select value={personelFiltre} onChange={e => setPersonelFiltre(e.target.value)}
               className="h-12 bg-white border-2 border-gray-100 rounded-2xl px-4 text-xs font-black text-gray-700 focus:outline-none focus:border-indigo-400 transition-colors shadow-sm min-w-[130px] appearance-none cursor-pointer">
               {['Hepsi', ...personelListesi].map(p => <option key={p} value={p}>{p}</option>)}
             </select>
             <select value={detayFiltre} onChange={e => setDetayFiltre(e.target.value)}
               className="h-12 bg-white border-2 border-gray-100 rounded-2xl px-4 text-xs font-black text-gray-700 focus:outline-none focus:border-indigo-400 transition-colors shadow-sm appearance-none cursor-pointer">
               <option value="Göster">Detaylar: Açık</option>
               <option value="Gösterme">Detaylar: Kapalı</option>
             </select>
          </div>
        </div>
        <div className="flex items-center pt-7">
           <label className="flex items-center gap-3 cursor-pointer group">
              <input type="checkbox" checked={sifirGizle} onChange={e => setSifirGizle(e.target.checked)} className="w-5 h-5 accent-indigo-600 rounded-lg transition-all" />
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest group-hover:text-gray-600 transition-colors">Sıfırları Gizle</span>
           </label>
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-4 sm:ml-auto">
           {paraBirimiListesi.map(pb => (
             <button key={pb} onClick={() => setParaBirimi(pb)}
               className={`h-10 px-5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                 paraBirimi === pb ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-white border-2 border-gray-100 text-gray-500 hover:border-indigo-200'
               }`}>
               {pb}
             </button>
           ))}
           <div className="h-10 w-[2px] bg-gray-100 mx-1" />
           <button onClick={filtrele} className="h-12 px-8 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 transition-all active:scale-95">Uygula</button>
           <button onClick={temizle} className="h-12 w-12 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-2xl flex items-center justify-center transition-all active:rotate-180">🔄</button>
        </div>
      </div>

      {yukleniyor ? (
        <LoadingSpinner fullPage text="Veriler Derleniyor" color="indigo" size="lg" />
      ) : (
        <div className="flex flex-col xl:flex-row gap-8 items-start">
          
          {/* ── Main Growth Table ─────────────────────────────────────────── */}
          <div className="flex-[3] min-w-0 w-full bg-white rounded-[3rem] border-2 border-gray-50 shadow-2xl overflow-hidden">
            <div className="overflow-x-auto custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="text-left py-6 px-8 font-black text-gray-400 text-[10px] uppercase tracking-[0.2em]">Tarih</th>
                    {detayGoster && (
                      <>
                        <th className="text-right py-6 px-4 font-black text-orange-400 text-[10px] uppercase tracking-widest">Adisyon</th>
                        <th className="text-right py-6 px-4 font-black text-indigo-400 text-[10px] uppercase tracking-widest">Ek Gelir</th>
                        <th className="text-right py-6 px-4 font-black text-blue-400 text-[10px] uppercase tracking-widest">Ödemeler</th>
                      </>
                    )}
                    <th className="text-right py-6 px-4 font-black text-emerald-500 text-[10px] uppercase tracking-widest">Toplam Gelir</th>
                    <th className="text-right py-6 px-4 font-black text-rose-500 text-[10px] uppercase tracking-widest">Gider</th>
                    <th className="text-right py-6 px-8 font-black text-gray-900 text-[10px] uppercase tracking-widest border-l border-gray-50">Net Kasa</th>
                  </tr>
                </thead>
                <tbody>
                  {gunler.filter(iso => !sifirGizle || gunOzet(iso).toplam > 0 || gunOzet(iso).gider > 0).map(iso => {
                    const oz = gunOzet(iso);
                    const bugunMu = iso === bugunISO();
                    return (
                      <tr key={iso} className={`border-b border-gray-50 transition-all hover:bg-gray-50/50 group ${bugunMu ? 'bg-indigo-50/30' : ''}`}>
                        <td className={`py-5 px-8 text-xs font-black ${bugunMu ? 'text-indigo-600' : 'text-gray-700'} tracking-tighter`}>{ddmmyyyy(iso)} {bugunMu && <span className="ml-2 px-2 py-0.5 bg-indigo-100 rounded-full text-[8px] tracking-widest uppercase">Bugün</span>}</td>
                        {detayGoster && (
                          <>
                            <td className="py-5 px-4 text-right">
                              <div className="flex items-center justify-end gap-2 group/col">
                                <span className={`text-[11px] font-bold ${oz.adisyon > 0 ? 'text-orange-600' : 'text-gray-300'}`}>{oz.adisyon.toLocaleString('tr-TR')}</span>
                                {oz.adisyon > 0 && <button onClick={() => setSecilenGun({iso, tip:'adisyon'})} className="opacity-100 sm:opacity-0 sm:group-hover/col:opacity-100 transition-opacity"><EyeIcon color="#f97316" /></button>}
                              </div>
                            </td>
                            <td className="py-5 px-4 text-right">
                              <div className="flex items-center justify-end gap-2 group/col">
                                <span className={`text-[11px] font-bold ${oz.gelir > 0 ? 'text-indigo-600' : 'text-gray-300'}`}>{oz.gelir.toLocaleString('tr-TR')}</span>
                                {oz.gelir > 0 && <button onClick={() => setSecilenGun({iso, tip:'gelir', gelirTipFiltre:'gelir'})} className="opacity-100 sm:opacity-0 sm:group-hover/col:opacity-100 transition-opacity"><EyeIcon color="#6366f1" /></button>}
                              </div>
                            </td>
                            <td className="py-5 px-4 text-right">
                              <div className="flex items-center justify-end gap-2 group/col">
                                <span className={`text-[11px] font-bold ${oz.rezervasyon > 0 ? 'text-blue-600' : 'text-gray-300'}`}>{oz.rezervasyon.toLocaleString('tr-TR')}</span>
                                {oz.rezervasyon > 0 && <button onClick={() => setSecilenGun({iso, tip:'gelir', gelirTipFiltre:'rezervasyon'})} className="opacity-100 sm:opacity-0 sm:group-hover/col:opacity-100 transition-opacity"><EyeIcon color="#3b82f6" /></button>}
                              </div>
                            </td>
                          </>
                        )}
                        <td className="py-5 px-4 text-right">
                          <div className="flex items-center justify-end gap-2 group/col">
                            <span className={`text-[11px] font-black ${oz.toplam > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>{oz.toplam.toLocaleString('tr-TR')}</span>
                            {oz.toplam > 0 && <button onClick={() => setSecilenGun({iso, tip:'gelir', gelirTipFiltre:null})} className="opacity-100 sm:opacity-0 sm:group-hover/col:opacity-100 transition-opacity"><EyeIcon color="#10b981" /></button>}
                          </div>
                        </td>
                        <td className="py-5 px-4 text-right">
                          <div className="flex items-center justify-end gap-2 group/col">
                            <span className={`text-[11px] font-black ${oz.gider > 0 ? 'text-rose-500' : 'text-gray-300'}`}>{oz.gider.toLocaleString('tr-TR')}</span>
                            {oz.gider > 0 && <button onClick={() => setSecilenGun({iso, tip:'gider'})} className="opacity-100 sm:opacity-0 sm:group-hover/col:opacity-100 transition-opacity"><EyeIcon color="#f43f5e" /></button>}
                          </div>
                        </td>
                        <td className={`py-5 px-8 text-right text-xs font-black border-l border-gray-50 tracking-tighter ${oz.kasa < 0 ? 'text-rose-600' : 'text-gray-900 group-hover:text-indigo-600 transition-colors'}`}>
                          {oz.kasa.toLocaleString('tr-TR',{minimumFractionDigits:2})}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total Row */}
                  <tr className="bg-gray-50/50 border-t-4 border-white font-black">
                    <td className="py-8 px-8 text-[11px] uppercase tracking-widest text-gray-500">Aylık Toplam</td>
                    {detayGoster && (
                      <>
                        <td className="py-8 px-4 text-right text-orange-600 font-extrabold">{toplamAdisyon.toLocaleString('tr-TR')}</td>
                        <td className="py-8 px-4 text-right text-indigo-600 font-extrabold">{toplamEk.toLocaleString('tr-TR')}</td>
                        <td className="py-8 px-4 text-right text-blue-600 font-extrabold">{toplamRez.toLocaleString('tr-TR')}</td>
                      </>
                    )}
                    <td className="py-8 px-4 text-right text-emerald-600 text-base">{toplamGelir.toLocaleString('tr-TR')}</td>
                    <td className="py-8 px-4 text-right text-rose-600 text-base">{toplamGider.toLocaleString('tr-TR')}</td>
                    <td className={`py-8 px-8 text-right text-lg border-l border-white ${toplamKasa < 0 ? 'text-rose-600' : 'text-indigo-600'}`}>{toplamKasa.toLocaleString('tr-TR',{minimumFractionDigits:2})}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Side Analyis Panels ───────────────────────────────────────── */}
          <div className="w-full xl:w-80 flex-shrink-0 space-y-8">
            
            {/* Payment Distribution */}
            <div className="bg-white rounded-[2.5rem] border-2 border-gray-50 shadow-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-sm shadow-inner">📥</div>
                <h3 className="font-black text-gray-800 text-[10px] uppercase tracking-widest leading-none">Ödeme Dağılımı</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(gelirTipMap).length === 0 ? (
                  <p className="text-center py-6 text-[10px] font-black uppercase text-gray-300 tracking-widest">Veri Bulunamadı</p>
                ) : Object.entries(gelirTipMap).sort((a,b)=>b[1]-a[1]).map(([tip, val]) => (
                  <div key={tip} className="flex justify-between items-center p-3 rounded-2xl border border-gray-50 group hover:border-emerald-100 transition-colors">
                    <span className="text-[10px] font-black text-gray-400 group-hover:text-gray-600 transition-colors uppercase pr-2 truncate">{tip}</span>
                    <span className="text-xs font-black text-gray-900 tracking-tighter shrink-0">{val.toLocaleString('tr-TR')} <span className="text-[9px] opacity-40">{paraBirimi}</span></span>
                  </div>
                ))}
              </div>
            </div>

            {/* Expense Distribution */}
            <div className="bg-white rounded-[2.5rem] border-2 border-gray-50 shadow-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center text-sm shadow-inner">📤</div>
                <h3 className="font-black text-gray-800 text-[10px] uppercase tracking-widest leading-none">Gider Dağılımı</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(giderTipMap).length === 0 ? (
                  <p className="text-center py-6 text-[10px] font-black uppercase text-gray-300 tracking-widest">Veri Bulunamadı</p>
                ) : Object.entries(giderTipMap).sort((a,b)=>b[1]-a[1]).map(([tip, val]) => (
                  <div key={tip} className="flex justify-between items-center p-3 rounded-2xl border border-gray-100 group hover:border-rose-100 transition-colors">
                    <span className="text-[10px] font-black text-gray-400 group-hover:text-gray-600 transition-colors uppercase pr-2 truncate">{tip}</span>
                    <span className="text-xs font-black text-gray-900 tracking-tighter shrink-0">{val.toLocaleString('tr-TR')} <span className="text-[9px] opacity-40">{paraBirimi}</span></span>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Recap Card */}
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-[2.5rem] shadow-2xl p-8 text-white relative overflow-hidden group">
               <div className="absolute right-[-20px] bottom-[-20px] text-8xl opacity-10 group-hover:scale-110 transition-transform">💎</div>
               <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-6 flex items-center gap-2">
                 Finansal Özet <span className="w-6 h-[1px] bg-white/30"></span>
               </p>
               <div className="space-y-6">
                  <div>
                    <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-1">Aylık Tahsilat</p>
                    <p className="text-2xl font-black tracking-tighter">{toplamGelir.toLocaleString('tr-TR')} <span className="text-xs opacity-50 underline decoration-indigo-300 transition-all">{paraBirimi}</span></p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-1">Aylık Gider</p>
                    <p className="text-2xl font-black tracking-tighter">{toplamGider.toLocaleString('tr-TR')} <span className="text-xs opacity-50 underline decoration-rose-400 transition-all">{paraBirimi}</span></p>
                  </div>
                  <div className="pt-6 border-t border-white/10">
                    <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-1">Net Nakit Akışı</p>
                    <p className={`text-3xl font-black tracking-tighter ${toplamKalan < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{toplamKalan.toLocaleString('tr-TR',{minimumFractionDigits:2})} <span className="text-xs opacity-50 tracking-normal">{paraBirimi}</span></p>
                  </div>
               </div>
            </div>

          </div>
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {secilenGun && secilenGun.tip === 'adisyon' && (
        <AdisyonPopup iso={secilenGun.iso} adisyonlar={adisyonlar} onClose={() => setSecilenGun(null)} dovizCevir={dovizCevir} paraBirimi={paraBirimi} />
      )}
      {secilenGun && secilenGun.tip === 'gelir' && (
        <GelirPopup iso={secilenGun.iso} gelirTipFiltre={secilenGun.gelirTipFiltre} kasaGelirler={kasaGelirler} odemeler={odemeler} onClose={() => setSecilenGun(null)} dovizCevir={dovizCevir} paraBirimi={paraBirimi} gelirSil={gelirSil} odemeSil={odemeSil} />
      )}
      {secilenGun && secilenGun.tip === 'gider' && (
        <GiderPopup iso={secilenGun.iso} giderler={giderler} onClose={() => setSecilenGun(null)} dovizCevir={dovizCevir} paraBirimi={paraBirimi} giderSil={giderSil} />
      )}

      {/* ── Alt Bilgi ── */}
      <UsageInfo
        id="kasa-aylik"
        color="indigo"
        title="Nasıl Kullanılır?"
        description="Aylık kasa sayfasında otelinizin gelir, gider ve adisyon verilerini takvim bazlı görebilirsiniz. Ay bazında kârlılık durumunuzu ve nakit akışınızı buradan takip edebilirsiniz."
        scenarios={[
          { title: "Günlük Detaylar", content: "Takvim üzerindeki herhangi bir güne tıklayarak o günkü tüm gelir ve gider kalemlerini liste halinde görebilirsiniz." },
          { title: "Tip Bazlı Analiz", content: "Üst paneldeki filtrelerle sadece nakit, banka veya kredi kartı işlemlerine odaklanarak kasa mutabakatı yapabilirsiniz." },
          { title: "Performans Takibi", content: "Alt paneldeki Net Nakit Akışı göstergesi ile seçili ayın finansal başarısını anlık olarak izleyebilirsiniz." }
        ]}
      />
    </div>
  );
}